import instance from "../Utils/instance";
import { ErrorHandler, SITE_STORAGE } from "../Utils/Constants.";

const getUrlConfig = (type, url, data, headerData) => {
  let headers = {};
  headers["Accept"] = "application/json";
  headers["Content-Type"] = "application/json";
  if (Object.keys(headerData).length > 0) {
    Object.keys(headerData).map((singleHeader, i) => {
      return (headers[singleHeader] = headerData[singleHeader]);
    });
  }

  let config = {
    method: type,
    url: url,
    data: data,
    headers: headers,
  };
  return config;
};

/**
 * @description convert a backend field key to the Formik field name
 * e.g. "ZoneName" -> "zoneName", "TimeZoneDetails.Id" -> "timeZoneDetails", "$.zoneNumber" -> "zoneNumber"
 */
const toFieldName = (key) => {
  let name = key.replace(/^\$\./, "").split(".")[0].split("[")[0];
  return name.charAt(0).toLowerCase() + name.slice(1);
};

/**
 * @description read the errors map of a 400 ValidationProblemDetails into { field: "first message" }
 */
const getFieldErrors = (errors) => {
  let fieldErrors = {};
  if (errors && typeof errors === "object") {
    Object.keys(errors).forEach((key) => {
      let messages = errors[key];
      let message = Array.isArray(messages) ? messages[0] : messages;
      let field = toFieldName(key);
      if (field && message && !fieldErrors[field]) {
        fieldErrors[field] = message;
      }
    });
  }
  return fieldErrors;
};

/**
 * @description map an HTTP status to the kind of result screens can branch on
 */
const getErrorKind = (status) => {
  if (status === 400) return "validation";
  if (status === 401) return "auth";
  if (status === 403) return "forbidden";
  if (status === 404) return "notFound";
  if (status === 409) return "conflict";
  return "server";
};

/**
 * @description api call wrapper
 * data / error keep their original meaning for existing screens.
 * ok, status, kind, message, title, detail, fieldErrors are added for E5 screens
 * and come from the real HTTP status (409 / 404 / 400 carry a ProblemDetails body).
 */
export const serviceProvider = async (
  fetchType,
  fetchURL,
  formData,
  Header
) => {
  let resultResponse = {
    data: "",
    error: "",
    ok: false,
    status: 0,
    kind: "",
    message: "",
    title: "",
    detail: "",
    fieldErrors: {},
  };
  const url = fetchURL;
  const type = fetchType;
  const data = formData;
  const headerData = Header;

  let config = getUrlConfig(type, url, data, headerData);
  await instance(config)
    .then((resp) => {
      if (resp.status === 200) {
        resultResponse.data = resp.data;
        resultResponse.error = false;

        // old envelope: the real outcome may sit in the body as httpStatusCode
        let body = resp.data && typeof resp.data === "object" ? resp.data : {};
        let bodyStatus = body.httpStatusCode ? body.httpStatusCode : 200;
        resultResponse.status = bodyStatus;
        resultResponse.message = body.message ? body.message : "";
        resultResponse.ok = bodyStatus === 200;
        resultResponse.kind = bodyStatus === 200 ? "success" : "business";
      } else if (resp.status === 401) {
        resultResponse.error = true;
      } else if (resp.status === 403) {
        SITE_STORAGE.removeItem("userData");
      }
    })
    .catch(function (error) {
      resultResponse.error = true;
      if (error && error.response) {
        // 4xx / 5xx: body is ProblemDetails (409 / 404 / 500) or ValidationProblemDetails (400)
        let status = error.response.status;
        let body =
          error.response.data && typeof error.response.data === "object"
            ? error.response.data
            : {};
        resultResponse.status = status;
        resultResponse.kind = getErrorKind(status);
        resultResponse.title = body.title ? body.title : "";
        resultResponse.detail = body.detail ? body.detail : "";
        resultResponse.fieldErrors = getFieldErrors(body.errors);
      } else if (error && error.code === "ECONNABORTED") {
        resultResponse.kind = "timeout";
      } else {
        resultResponse.kind = "network";
      }
      ErrorHandler(error);
    });
  return resultResponse;
};

/**
 * @description result for a call blocked before it reaches the API
 */
const blockedResult = (kind, detail) => ({
  data: "",
  error: true,
  ok: false,
  status: 0,
  kind: kind,
  message: "",
  title: "",
  detail: detail,
  fieldErrors: {},
});

/**
 * @description POST { id, deactivatedBy } for E5 deactivate / reactivate endpoints.
 * Blocks the call when the id is not a positive integer or no user is logged in.
 * @param {*endpoint e.g. Zone/Deactivate} url
 * @param {*zone id, network id or mznl id (for mapping endpoints)} id
 * @param {* Bearer token for authentication} headerData
 */
export const statusChangeRequest = async (url, id, headerData) => {
  if (!Number.isInteger(id) || id <= 0) {
    return blockedResult("validation", "Invalid record id.");
  }

  let userData = null;
  try {
    userData = JSON.parse(SITE_STORAGE.getItem("userData"));
  } catch (e) {
    userData = null;
  }
  if (!userData || !userData.id) {
    return blockedResult("auth", "");
  }

  return await serviceProvider(
    "POST",
    url,
    { id: id, deactivatedBy: userData.id },
    headerData ? headerData : {}
  );
};
